import { useMemo } from 'react';
import { getUser } from '../services/auth';
import {
  hasPermission as _hasPermission,
  hasAnyPermission as _hasAnyPermission,
  hasAllPermissions as _hasAllPermissions,
  getVisibleSectors,
  getVisibleModules,
  getVisibleTabs,
  podeVer,
} from '../utils/permissions';

/**
 * 🔐 Hook de permissões RBAC
 * 
 * Uso:
 * ```tsx
 * const { hasPermission, canView, visibleSectors, isAdmin } = usePermissions();
 * 
 * if (hasPermission('web:corporativo:cadastros:alunos:view')) { ... }
 * if (canView('corporativo', 'cadastros', 'alunos')) { ... }
 * ```
 */
export function usePermissions() {
  const user = getUser();
  const rawPermissoes = user?.permissoes || [];
  const isAdmin = user?.domAdministrador === true;

  // Estabilizar referência do array de permissões usando conteúdo como chave.
  // getUser() faz JSON.parse a cada chamada → nova referência do array a cada render.
  // Sem isso, useMemo recria todas as funções, causando cascata de re-renders
  // e re-execução de useEffects que dependem de canView/canSeeTabCorp/etc.
  const permKey = rawPermissoes.join(',');
  const permissoes = useMemo(() => rawPermissoes, [permKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => ({
    /** Permissões brutas do usuário */
    permissoes,

    /** Se é superadmin (bypass total) */
    isAdmin,

    /** Verifica uma permissão específica */
    hasPermission: (perm: string) => isAdmin || _hasPermission(permissoes, perm),

    /** Verifica se tem QUALQUER uma das permissões (OR) */
    hasAny: (perms: string[]) => isAdmin || _hasAnyPermission(permissoes, perms),

    /** Verifica se tem TODAS as permissões (AND) */
    hasAll: (perms: string[]) => isAdmin || _hasAllPermissions(permissoes, perms),

    /**
     * Verifica se pode visualizar um recurso por path hierárquico.
     * canView('corporativo') → tem alguma permissão no setor corporativo?
     * canView('corporativo', 'cadastros') → tem alguma no módulo cadastros?
     * canView('corporativo', 'cadastros', 'alunos') → tem permissão na aba alunos?
     */
    canView: (setor: string, modulo?: string, aba?: string): boolean =>
      isAdmin || podeVer(permissoes, setor, modulo, aba),

    /** Setores visíveis (ex: ['corporativo', 'escolar']) */
    visibleSectors: isAdmin
      ? ['corporativo', 'escolar', 'fretamento']
      : getVisibleSectors(permissoes),

    /** Módulos visíveis dentro de um setor */
    getVisibleModules: (setor: string) =>
      isAdmin
        ? setor === 'corporativo' ? ['cadastros', 'processos', 'relatorios'] : []
        : getVisibleModules(permissoes, setor),

    /** Abas visíveis dentro de um módulo */
    getVisibleTabs: (setor: string, modulo: string) =>
      isAdmin ? [] // admin vê todas — não precisa filtrar
        : getVisibleTabs(permissoes, setor, modulo),

    /** Dados do usuário */
    user,
  }), [permissoes, isAdmin]); // user excluído dos deps — conteúdo relevante já capturado via permissoes + isAdmin
}
